<template>
    <div :style="{ width: customWidth }">
        <vue-select
            v-model="selected"
            :options="options"
            :label="textField"
            class="st-autocomplete form-control"
            :name="name"
            :class="customClass"
            :reduce="el => el[valueField]"
            :clearable="clearable"
            :disabled="disabled"
            :placeholder="placeholder"
            :filter="filterOptions"
            :multiple="multiple"
            :tabindex="Number(tabindex)"
            :append-to-body="appendToBody"
        >
            <template #no-options="{}">
                {{ $t('GENERAL.AUTOCOMPLETE_NO_OPTIONS') }}
            </template>
        </vue-select>
    </div>
</template>

<script>
    import vSelect from 'vue-select';
    export default {
        inheritAttrs: false,
        name: 'StAutocomplete',
        components: {
            'vue-select': vSelect,
        },
        props: {
            value: {
                type: [String, Array, Number],
                default: ''
            },
            options: {
                type: Array,
                default: () => [],
            },
            name: {
                type: String,
                required: true,
            },
            textField: {
                type: String,
                default: 'text',
            },
            valueField: {
                type: [String, Array, Number],
                default: 'value',
            },
            customClass: String,
            customWidth: {
                type: String,
                default: 'auto',
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            reference: String,
            clearable: {
                type: Boolean,
                default: false,
            },
            placeholder: {
                type: String,
                default: '',
            },
            fields: Object,
            model: Object,
            formValidation: Object,
            multiple: {
                type: Boolean,
                default: false
            },
            tabindex: {
                type: String,
                default: null
            },
            appendToBody: {
                type: Boolean,
                default: false
            },
        },
        data() {
            return {
                field: this.fields?.[this.name],
            }
        },
        watch: {
            options(newValue) {
                if (this.field?.autocompleteRequired) {
                    if (!this.field.enabledValidation) {
                        this.formValidation[newValue.length ? 'enableValidator' : 'disableValidator'](this.field.name);
                    }

                    this.formValidation.resetField(this.field.name, true);
                }
            },
            selected(newValue, oldValue) {
                if (newValue === oldValue) return false;
                if (newValue && this.field?.autocompleteRequired) {
                    this.formValidation.revalidateField(this.field.name, true);
                }
            },
        },
        computed: {
            selected: {
                get() {
                    return this.value;
                },
                set(val) {
                    this.$emit('input', val);
                    this.$emit('change', val);
                }
            },
            hasParents() {
                return !!this.field?.parentFields.length;
            },
        },
        methods: {
            addWatchers() {
                const callbackFn = this.watcherCallbackFn;
                this.field.parentFields.forEach((el) => {
                    this.$watch(`model.${el.name}`, callbackFn, { deep: true });

                    this.field.fetchParams = { [el.key]: this.model[el.name] };
                    this.field.updateOptions();
                }, this);

            },
            watcherCallbackFn(newValue, oldValue) {
                if (newValue === oldValue) return false;
                this.model[this.field.name] = '';
                // this.field.fetchParams = { parent_id: newValue };
                // this.field.updateOptions();

                this.field.parentFields.forEach((el) => {
                    this.field.fetchParams = { [el.key]: newValue };
                    this.field.updateOptions();
                }, this);
            },
            /**
             * Filter options from start only
             * Usign unicode normalization form of the options text
             */
            filterOptions(options, search) {
                return options.filter((el) => {
                    const normalized = el[this.textField].toLowerCase().normalize('NFKD');
                    const normalizedOnlyWord = normalized.replace(/[^\w]/g, '');
                    const searchLowerCase = search.toLowerCase();
                    return normalized.includes(searchLowerCase)
                        || normalizedOnlyWord.includes(searchLowerCase);
                });
            },
        },
        async mounted() {
            if (this.field) {
                await this.field.updateOptions();
            }
            if (this.hasParents) {
                this.addWatchers();
            }

            // Add native keydown listener to the input inside vue-select
            this.$nextTick(() => {
                const input = this.$el.querySelector('input');
                if (input) {
                    input.addEventListener('keydown', (e) => {
                        if (e.key === 'Enter') {
                            const vueSelectInstance = this.$children?.[0];
                            
                            // Check if there's a highlighted option (keyboard navigation)
                            if (vueSelectInstance && vueSelectInstance.open && vueSelectInstance.typeAheadPointer > -1) {
                                const filteredOptions = vueSelectInstance.filteredOptions;
                                if (filteredOptions && filteredOptions[vueSelectInstance.typeAheadPointer]) {
                                    const highlightedOption = filteredOptions[vueSelectInstance.typeAheadPointer];
                                    const value = typeof highlightedOption === 'object' 
                                        ? highlightedOption[this.valueField] 
                                        : highlightedOption;
                                    
                                    if (this.multiple) {
                                        // For multiple: add to array if not already present
                                        const currentValues = Array.isArray(this.selected) ? [...this.selected] : [];
                                        if (!currentValues.includes(value)) {
                                            currentValues.push(value);
                                            this.selected = currentValues;
                                        }
                                    } else {
                                        // For single: just set the value
                                        this.selected = value;
                                    }
                                    
                                    // Emit with the correct value
                                    this.$emit('enter', value);
                                    e.preventDefault();
                                    e.stopPropagation();
                                    e.stopImmediatePropagation();   
                                    return;
                                }
                            }
                            
                            // Fallback: emit current selected value
                            this.$emit('enter', this.selected);   
                            e.preventDefault();
                            e.stopPropagation();
                            e.stopImmediatePropagation();                         
                        }
                    }, true); // Use capture phase to intercept before vue-select
                }
            });
        },
        beforeDestroy() {
            if (this.field && this.field.fetchParams) {
                this.field.fetchParams = {};
            }
        },
    };
</script>
